import React from 'react'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import { SwitchComponent } from '@syncfusion/ej2-react-buttons'
import {
  DropDownButtonComponent,
  ItemModel,
} from '@syncfusion/ej2-react-splitbuttons'
import { Button, DataGridComponent } from '@components/common'
import { ICampaignTriggerKeyword } from '@dataTypes/marketing.type'
import { compareCampaignTriggerKeywordIds } from '@utils/marketing.utils'

interface ActionTriggerComponentProps {
  data: ICampaignTriggerKeyword[]
  disableDuplicate: boolean
  handleView: (data: ICampaignTriggerKeyword) => void
  setData: (data: ICampaignTriggerKeyword[]) => void
  handleEdit: (data: ICampaignTriggerKeyword) => void
}

const ActionTriggerComponent: React.FC<ActionTriggerComponentProps> = ({
  data,
  disableDuplicate,
  handleView,
  handleEdit,
  setData,
}) => {
  // Handle switch toggle
  const handleToggle = (
    checked: boolean,
    rowData: ICampaignTriggerKeyword,
  ): void => {
    const updatedData = data.map((item) =>
      compareCampaignTriggerKeywordIds(
        item.campaign_trigger_keyword_id,
        rowData.campaign_trigger_keyword_id,
      )
        ? { ...item, status: checked ? 'Active' : 'Draft' }
        : item,
    )
    setData(updatedData)
  }

  const validateActionTrigger = (props: ICampaignTriggerKeyword): boolean => {
    const errors: { [key: string]: string } = {}
    if (!props.name || props.name.trim() === '') {
      errors.name = 'Trigger name is required'
    }
    if (!props.keyword || props.keyword === '') {
      errors.keyword = 'Keyword is required'
    }
    if (!props.text_content || props.text_content.trim() === '') {
      errors.text_content = 'Input campaign message'
    }
    return Object.keys(errors).length === 0
  }

  // Template for Switch component inside Grid column
  const switchTemplate = (props: ICampaignTriggerKeyword): JSX.Element => {
    const isDisabled = !validateActionTrigger(props)
    return (
      <span
        data-bs-toggle={isDisabled ? 'tooltip' : undefined}
        title={
          isDisabled
            ? 'This trigger is still in draft mode. Complete the missing data to activate.'
            : undefined
        }
      >
        <SwitchComponent
          key={props.campaign_trigger_keyword_id}
          checked={props.status === 'Active'}
          change={(e) => handleToggle(e.checked as boolean, props)}
        />
      </span>
    )
  }

  // Action dropdown items
  const actionItems: ItemModel[] = [
    { text: 'Edit' },
    { text: 'Duplicate', disabled: disableDuplicate },
    { text: 'Delete' },
  ]

  // Handle dropdown actions
  const handleAction = (args: any, rowData: ICampaignTriggerKeyword): void => {
    const action = args.item.text

    if (action === 'Edit') {
      handleEdit(rowData)
    } else if (action === 'Duplicate') {
      if (disableDuplicate) return
      const newData = {
        ...rowData,
        campaign_trigger_keyword_id: `new-${new Date().getTime()}`,
      }
      setData([...data, newData])
    } else if (action === 'Delete') {
      const newData = data.filter(
        (t) =>
          !compareCampaignTriggerKeywordIds(
            t.campaign_trigger_keyword_id,
            rowData.campaign_trigger_keyword_id,
          ),
      )
      setData(newData)
    }
  }
  const handleViewTriggerDetails = (props: ICampaignTriggerKeyword): void => {
    handleView(props)
  }

  // Action column template
  const actionTemplate = (props: ICampaignTriggerKeyword): JSX.Element => {
    return (
      <div
        key={props.campaign_trigger_keyword_id}
        className="actions-container"
      >
        <Button
          data-bs-toggle="tooltip"
          title="View trigger details"
          className="button-primary"
          onClick={() => handleViewTriggerDetails(props)}
        >
          <i className="mgc_eye_2_fill" />
        </Button>
        <DropDownButtonComponent
          items={actionItems}
          select={(args) => handleAction(args, props)}
          iconCss="ti ti-dots-vertical"
          cssClass="e-caret-hide" // hides the default arrow caret
        />
      </div>
    )
  }

  const columns: ColumnModel[] = [
    {
      headerText: 'Status',
      template: (props: ICampaignTriggerKeyword) => switchTemplate(props),
      width: 0.13,
      textAlign: 'Center',
    },
    {
      field: 'name',
      headerText: 'Trigger name',
      width: 0.36,
    },
    {
      field: 'keyword',
      headerTemplate: () => (
        <span className="e-headertext">
          Keyword
          <i
            data-bs-toggle="tooltip"
            className="mgc_information_fill"
            title={`Keywords trigger an automatic reply when a patient’s incoming message matches the word.`}
          />
        </span>
      ),
      width: 0.36,
    },
    {
      headerText: 'Actions',
      template: (props: ICampaignTriggerKeyword) => actionTemplate(props),
      width: 0.15,
      textAlign: 'Center',
    },
  ]

  return (
    <DataGridComponent
      key="action-trigger-data-grid"
      data={data}
      columns={columns}
    />
  )
}

export default ActionTriggerComponent
